import React, { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { StreamCard } from '../components/StreamCard';
import { Row } from '../components/Layout';

interface SimpleStreamsPageProps {
    onStreamStart?: () => void;
    onStreamStop?: () => void;
    onNavigate?: (page: string) => void;
    onWatchStream?: (id: string) => void;
    onDrawer?: () => void;
    onViewProfile?: (userId: string) => void;
}

interface Stream {
    id: string;
    name: string;
    tag: string;
    viewers: number | string;
    avatar: string | React.ReactNode;
    thumb: string;
    accent: string;
    topic: string;
    audio: boolean;
    streamKey?: string;
}

const CATEGORIES = ["Tümü", "Gaming", "Müzik", "Sohbet", "Spor", "Sanat", "Tech", "Eğitim"];

const SimpleStreamsPage: React.FC<SimpleStreamsPageProps> = ({ onStreamStart, onStreamStop, onNavigate, onWatchStream, onDrawer, onViewProfile }) => {
    const [cat, setCat] = useState("Tümü");
    const [streams, setStreams] = useState<Stream[]>([]);
    const [loading, setLoading] = useState(true);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const getRelativeTime = (dateString: string) => {
        if (!dateString) return '';
        const rtf = new Intl.RelativeTimeFormat('tr', { numeric: 'auto' });
        const timeDiff = new Date(dateString).getTime() - new Date().getTime();
        const daysDifference = Math.round(timeDiff / (1000 * 60 * 60 * 24));
        if (daysDifference === 0) {
            const hoursDifference = Math.round(timeDiff / (1000 * 60 * 60));
            if (hoursDifference === 0) {
                const minutesDifference = Math.round(timeDiff / (1000 * 60));
                return rtf.format(minutesDifference, 'minute');
            }
            return rtf.format(hoursDifference, 'hour');
        }
        return rtf.format(daysDifference, 'day');
    };

    React.useEffect(() => {
        const fetchStreams = async () => {
            try {
                const { streamAPI, notificationAPI } = await import('../services/api');
                const activeStreams = await streamAPI.getStreams();
                const formattedStreams: Stream[] = activeStreams.map((s: any) => ({
                    id: s._id,
                    name: s.streamer.username,
                    topic: s.title,
                    viewers: s.viewers,
                    avatar: s.streamer.profileImage ? <img src={s.streamer.profileImage} width="100%" height="100%" alt="avatar" style={{ borderRadius: '50%', objectFit: 'cover' }} /> : '🎭',
                    tag: 'Sohbet',
                    audio: false,
                    thumb: 'linear-gradient(135deg,rgba(0,229,204,.2),#0d1f2d)',
                    accent: '#00E5CC',
                    streamKey: s.streamKey
                }));
                setStreams(formattedStreams);

                const notifs = await notificationAPI.getNotifications();
                setNotifications(notifs);
                setUnreadCount(notifs.filter((n: any) => !n.read).length);
            } catch (err) {
                console.error("Failed to fetch streams or notifications:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStreams();

        import('../services/socket').then(({ socketService }) => {
            const socket = socketService.getSocket();
            if (!socket) return;
            socket.on('streamStarted', (newStreamData) => {
                setStreams(prev => [{
                    id: newStreamData.streamKey,
                    name: newStreamData.streamer,
                    topic: newStreamData.title,
                    viewers: 0,
                    avatar: '🎭',
                    tag: 'Sohbet',
                    audio: false,
                    thumb: 'linear-gradient(135deg,rgba(0,229,204,.2),#0d1f2d)',
                    accent: '#00E5CC',
                    streamKey: newStreamData.streamKey
                }, ...prev]);
            });
            socket.on('streamStopped', (stoppedData) => {
                setStreams(prev => prev.filter(s => s.name !== stoppedData.streamer));
            });
            socketService.onNewNotification((notif: any) => {
                setNotifications(prev => [notif, ...prev]);
                setUnreadCount(prev => prev + 1);
            });
        });
    }, []);

    const openNotifications = () => {
        setShowNotifications(true);
        if (unreadCount > 0) {
            import('../services/api').then(({ notificationAPI }) => {
                notificationAPI.markAsRead();
                setUnreadCount(0);
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            });
        }
    };

    const filtered = cat === "Tümü" ? streams : streams.filter(s => s.tag === cat);

    const handleStreamClick = (stream: any) => {
        if (onWatchStream) {
            onWatchStream(stream.streamKey || stream.id.toString());
        } else if (onNavigate) {
            onNavigate('watch');
        }
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', animation: 'slideR .4s ease' }}>
            <PageHeader
                title={<><span>Buddy</span><span style={{ color: 'var(--coral)' }}>Live</span></>}
                subtitle="Merhaba 👋"
                onDrawer={onDrawer}
                right={
                    <>
                        <button onClick={() => onNavigate && onNavigate('discover')} className="icon-btn" style={{ fontSize: 16 }}>🔍</button>
                        <button onClick={openNotifications} className="icon-btn" style={{ fontSize: 16, position: 'relative' }}>
                            🔔
                            {unreadCount > 0 && (
                                <div style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, background: 'var(--coral)', borderRadius: '50%', border: '2px solid var(--bg)' }} />
                            )}
                        </button>
                    </>
                }
            />
            {/* Featured */}
            <div style={{ padding: '0 18px', flexShrink: 0 }}>
                {streams.length > 0 ? (
                    <div onClick={() => handleStreamClick(streams[0])} style={{
                        borderRadius: 22, overflow: 'hidden', cursor: 'pointer',
                        background: 'linear-gradient(135deg,#1a0a2e,#0d0d1a)', border: '1px solid rgba(168,85,247,.2)',
                        position: 'relative', height: 148, boxShadow: '0 14px 38px rgba(168,85,247,.14)'
                    }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 70% 30%,rgba(168,85,247,.28) 0%,transparent 60%)' }} />
                        <div style={{ position: 'absolute', inset: 0, padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                            <Row style={{ gap: 7, marginBottom: 7 }}>
                                <span className="live-badge"><span className="dot-live" />CANLI</span>
                                <span style={{ background: 'rgba(168,85,247,.22)', color: '#A855F7', fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 5, letterSpacing: '.06em', fontFamily: 'Syne' }}>ÖNE ÇIKAN</span>
                            </Row>
                            <div className="syne" style={{ fontSize: 15, fontWeight: 800, marginBottom: 3 }}>{streams[0].topic}</div>
                            <Row style={{ gap: 10 }}>
                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.55)' }}>{streams[0].name}</span>
                                <span style={{ color: '#A855F7', fontWeight: 600, fontSize: 11 }}>👁 {streams[0].viewers} izleyici</span>
                            </Row>
                        </div>
                        <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 56, opacity: .4 }}>🔥</div>
                    </div>
                ) : (
                    <div style={{
                        borderRadius: 22, overflow: 'hidden', background: 'var(--surface2)',
                        position: 'relative', height: 148, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 12
                    }}>
                        {loading ? 'Yayınlar yükleniyor...' : 'Şu an aktif yayın yok.'}
                    </div>
                )}
            </div>
            {/* Cats */}
            <div style={{ padding: '10px 0 5px', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: 7, padding: '0 18px', overflowX: 'auto', scrollbarWidth: 'none' }}>
                    {CATEGORIES.map(c => (
                        <button key={c} onClick={() => setCat(c)}
                            style={{
                                whiteSpace: 'nowrap', flexShrink: 0,
                                background: c === cat ? 'rgba(255,92,92,.15)' : 'var(--surface2)',
                                border: `1px solid ${c === cat ? 'rgba(255,92,92,.3)' : 'var(--border)'}`,
                                borderRadius: 8, fontSize: 11, color: c === cat ? 'var(--coral)' : 'var(--muted)',
                                padding: '5px 12px', cursor: 'pointer', transition: 'all .2s', fontFamily: 'DM Sans'
                            }}>
                            {c}
                        </button>
                    ))}
                </div>
            </div>
            {/* Grid */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 18px 80px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {filtered.map((s, i) => (
                        <div key={s.id} style={{ animation: `fadeUp .4s ease ${i * .05}s both` }}>
                            <StreamCard stream={s} onClick={handleStreamClick} />
                        </div>
                    ))}
                </div>
            </div>
        {/* Notifications Modal */}
        {showNotifications && (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.85)', zIndex: 1050,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                animation: 'fadeIn 0.2s ease-out'
            }}>
                <div style={{
                    background: 'var(--bg)', width: '100%', height: '70vh',
                    borderTopLeftRadius: 24, borderTopRightRadius: 24,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
                    animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                }}>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }} onClick={() => setShowNotifications(false)}>
                        <div style={{ width: 40, height: 4, background: 'var(--surface2)', borderRadius: 2 }} />
                    </div>
                    <div style={{ padding: '0 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
                        <h3 className="syne" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                            Bildirimler
                        </h3>
                        <div onClick={() => setShowNotifications(false)} style={{ cursor: 'pointer', padding: '5px', color: 'var(--muted)', fontSize: 20 }}>✕</div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', paddingBottom: 60 }}>
                        {notifications.length === 0 && (
                            <div style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 40, fontSize: 14 }}>
                                Henüz bildirim yok.
                            </div>
                        )}
                        {notifications.map((item: any) => (
                            <div 
                                key={item._id || Math.random()} 
                                style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20, cursor: (item.relatedUser || item.streamId) ? 'pointer' : 'default' }}
                                onClick={() => {
                                    if (item.type === 'stream' && item.streamId && onWatchStream) {
                                        setShowNotifications(false);
                                        onWatchStream(item.streamId);
                                    } else if (item.relatedUser && onViewProfile) {
                                        setShowNotifications(false);
                                        onViewProfile(item.relatedUser);
                                    }
                                }}
                            >
                                <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${item.color || 'var(--coral)'}22`, color: item.color || 'var(--coral)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                                    {item.icon || '🔔'}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, color: '#fff', lineHeight: 1.4, marginBottom: 4 }}>{item.text}</div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{getRelativeTime(item.createdAt)}</div>
                                </div>
                                {/* Unread Red Dot Indicator */}
                                {!item.read && (
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--coral)', alignSelf: 'center' }} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}
        </div>
    );
};

export default SimpleStreamsPage;
